import { Component, EventEmitter, Output, OnInit } from '@angular/core';
import { PolicyService } from '../../../../shared/common/services/policy.service';

interface PolicyGroup {
  id: number;
  name: string;
}

interface PolicyOption {
  id: number;
  label: string;
}

interface PolicyQuestion {
  id: number;
  key?: string;
  title: string;
  questionTypeId: number;
  options: PolicyOption[];
  currentValue?: string;
}

interface PolicySection {
  id: number;
  title: string;
  description?: string;
  questions: PolicyQuestion[];
}

interface SavePolicyAnswerRequest {
  policyQuestionId: number;
  policyGroupId: number | null;
  policyQuestionKey: string | null;
  policyValue: string;
}

enum PolicyQuestionType {
  YesNo = 1,
  Select = 2,
  MultiSelect = 3,
  Text = 4,
  MultipleChoice = 5,
  YesNoQ = 6,
  Goodwill = 7,
}

@Component({
  selector: 'app-dynamic-policies',
  templateUrl: './dynamic-policies.component.html',
  styleUrls: ['./dynamic-policies.component.scss'],
})
export class DynamicPoliciesComponent implements OnInit {
  private static readonly OPTION_DRIVEN_TYPES = new Set([
    PolicyQuestionType.Select,
    PolicyQuestionType.MultipleChoice,
    PolicyQuestionType.MultiSelect,
  ]);

  @Output() savePolicies = new EventEmitter<SavePolicyAnswerRequest[]>();

  policyGroups: PolicyGroup[] = [];
  sections: PolicySection[] = [];
  selectedGroupId?: number;

  QuestionType = PolicyQuestionType;

  readonly yesNoQOptions = [
    { value: 'y', label: 'y' },
    { value: 'n', label: 'n' },
    { value: 'q', label: 'q' },
  ] as const;

  readonly goodwillOptions = [
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
  ] as const;

  private saveTimers: Record<number, ReturnType<typeof setTimeout>> = {};

  constructor(private policyService: PolicyService) {}

  ngOnInit(): void {
    this.policyService.getPolicyGroups().subscribe((groups) => (this.policyGroups = groups));
  }

  onGroupChange(groupId: string): void {
    this.selectedGroupId = groupId ? +groupId : undefined;
    this.sections = [];

    if (!this.selectedGroupId) {
      return;
    }

    this.policyService
      .getPolicyGroupDetails(this.selectedGroupId)
      .subscribe((res) => (this.sections = res.sections ?? []));
  }

  hasAnswers(q: PolicyQuestion): boolean {
    if (DynamicPoliciesComponent.OPTION_DRIVEN_TYPES.has(q.questionTypeId)) {
      return !!q.options?.length;
    }

    return true;
  }

  sectionHasAnswerableQuestions(section: PolicySection): boolean {
    return section.questions.some((q) => this.hasAnswers(q));
  }

  isChecked(q: PolicyQuestion, optionId: number): boolean {
    return !!q.currentValue?.split(',').includes(optionId.toString());
  }

  toggleMulti(q: PolicyQuestion, optionId: number): void {
    const values = q.currentValue ? q.currentValue.split(',') : [];
    const s = optionId.toString();

    const idx = values.indexOf(s);
    if (idx >= 0) {
      values.splice(idx, 1);
    } else {
      values.push(s);
    }

    q.currentValue = values.join(',');
    this.saveAnswer(q);
  }

  onAnswerChanged(q: PolicyQuestion): void {
    this.saveAnswer(q);
  }

  onTextBlur(q: PolicyQuestion): void {
    this.saveAnswer(q);
  }

  onTextChanged(q: PolicyQuestion): void {
    clearTimeout(this.saveTimers[q.id]);

    this.saveTimers[q.id] = setTimeout(() => {
      this.saveAnswer(q);
    }, 400);
  }

  save(): void {
    const answers = this.buildPayload();
    this.policyService.savePolicyAnswers(answers).subscribe(() => {
      this.savePolicies.emit(answers);
    });
  }

  private saveAnswer(q: PolicyQuestion): void {
    if (!this.selectedGroupId) {
      return;
    }

    const payload: SavePolicyAnswerRequest[] = [
      {
        policyQuestionId: q.id,
        policyGroupId: this.selectedGroupId,
        policyQuestionKey: q.key ?? null,
        policyValue: q.currentValue ?? '',
      },
    ];

    this.policyService.savePolicyAnswers(payload).subscribe();
  }

  private buildPayload(): SavePolicyAnswerRequest[] {
    return this.sections.flatMap((s) =>
      s.questions.map((q) => ({
        policyQuestionId: q.id,
        policyGroupId: this.selectedGroupId ?? null,
        policyQuestionKey: q.key ?? null,
        policyValue: q.currentValue ?? '',
      }))
    );
  }
}
